// @ts-check

// @ts-ignore
import centrality from "ngraph.centrality";

import createGraph, { Graph } from "ngraph.graph";
// @ts-ignore
import pageRank from "ngraph.pagerank";

// @ts-ignore
import gexf from "ngraph.gexf"; // for gephi format

// custom centrality method
// import degree from "./centrality/degree";

/**
 * @typedef Link {object}
 * @property id {string}
 * @property source {string}
 * @property target {string}
 * @property count {number}
 */

/**
 * @typedef Node {object}
 * @property id {string}
 * @property label? {string}
 */

/**
 * 是否為 createGraph() 產生的 物件
 * 若 graph 為 createGraph()產生的物件,則會有對應的 methods
 * @param graph {any|object}
 * @returns {*}
 */
const isNgraphGraph = (graph) => graph && graph?.addNode && graph?.addLink;

/**
 * 將 links 塞進 graph instance 中
 * @param graph {Graph|undefined|null}
 * @param links {Link[]}
 * @returns {*}
 */
const addLinks = (graph, links) => {
  /**
   *
   * @type {Graph|undefined|null}
   */
  let tmpGraph = null;
  // todo: check if graph is graph instance
  if (isNgraphGraph(graph)) {
    tmpGraph = graph;
  } else {
    tmpGraph = createGraph();
  }
  if (Array.isArray(links)) {
    links.forEach((l) => {
      const { source, target, count } = l;
      if (source && target) {
        // @ts-ignore
        tmpGraph.addLink(source, target, { ...l, count });
      }
    });
  }
  return tmpGraph;
};

/**
 *
 * @param graph {Graph|undefined|null}
 * @param nodes {Node}
 * @returns {*}
 */
const addNodes = (graph, nodes) => {
  /**
   *
   * @type {Graph|undefined|null}
   */
  let tmpGraph;

  if (isNgraphGraph(graph)) {
    tmpGraph = graph;
  } else {
    tmpGraph = createGraph();
  }
  if (Array.isArray(nodes)) {
    nodes.forEach((n) => {
      const { id } = n;
      // @ts-ignore
      tmpGraph.addNode(id, { ...n });
    });
  }
  return tmpGraph;
};

/**
 *
 * @param graph {Graph}
 * @returns {object}
 */
const getDegreeCentrality = (graph) => centrality.degree(graph);

/**
 *
 * @param graph {Graph}
 * @returns {object}
 */
const getBetweenCentrality = (graph) => centrality.betweenness(graph);

/**
 *
 * @param graph {Graph}
 * @returns {object}
 */
const getCloseCentrality = (graph) => centrality.closeness(graph);

/**
 * Eccentricity centrality
 * @param graph {Graph}
 * @returns {object}
 */
const getEccentricityCentrality = (graph) => centrality.eccentricity(graph);

/**
 * Eigenvector centrality
 * @param graph {Graph}
 * @returns {object}
 */
const getEigenvectorCentrality = (graph) => pageRank(graph);

/**
 * Page rank
 * @param graph {Graph}
 * @returns {any}
 */
const getPageRank = (graph) => pageRank(graph);

/**
 *
 * @param acc {number}
 * @param cur {number}
 * @returns {number}
 */
const getTotalReduce = (acc, cur) => acc + cur;

/**
 *
 * @param map {object}
 * @returns {import('./snaAnalysis').ValueList}
 */
const getIdValueList = (map) =>
  Object.entries(map).map(([key, val]) => ({
    id: key,
    value: val,
    label: key,
  }));

/**
 *
 * @param data {import('./snaAnalysis').ValueList}
 * @returns {import('./snaAnalysis').ValueList}
 */
// @ts-ignore
const orderByValue = (data) => data.sort((a, b) => b.value - a.value);

/**
 *
 * @param data {import('./snaAnalysis').ValueList}
 * @returns {import('./snaAnalysis').ValueTransformList}
 */
const transformList = (data) =>
  data.map((o) => ({
    ...o,
    value: String(o.value.toFixed(8)),
  }));

class GraphAnalysis {
  /**
   *
   * @param links {Link[]}
   * @param nodes {Node[]}
   */
  constructor(links, nodes) {
    this.links = links;
    this.nodes = nodes;
    this.blankGraph = createGraph();
    // @ts-ignore
    this.graph = addNodes(addLinks(this.blankGraph, this.links), this.nodes);
    this.degreeC = null;
    this.betweenC = null;
    this.closeC = null;
    this.eccenC = null;
    this.egvC = null;
    this.pageRank = null;
  }

  /**
   * 度中心性(degree centrality ) - 準備 degreeC
   * @returns {object}
   */
  getDegreeCentralityPre() {
    let degreeC;
    if (!this.degreeC) {
      degreeC = getDegreeCentrality(this.graph);
      this.degreeC = degreeC;
    } else {
      degreeC = this.degreeC;
    }
    return degreeC;
  }

  /**
   * 度中心性(degree centrality )
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getDegreeCentrality() {
    const degreeC = this.getDegreeCentralityPre();
    return transformList(orderByValue(getIdValueList(degreeC)));
  }

  /**
   * 度中心性(degree centrality )-標準化
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getDegreeCentralityStd() {
    const degreeC = this.getDegreeCentralityPre();
    // @ts-ignore
    // const total = Object.values(degreeC).reduce(getTotalReduce, 0);
    const nodeCount = Object.values(degreeC).length;

    const resStd = Object.entries(degreeC).reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key] = (val || 0) / ((nodeCount - 1) * (nodeCount - 2));
      return acc;
    }, {});
    return transformList(orderByValue(getIdValueList(resStd)));
  }

  /**
   * 介數中心性(betweenness centrality) - 準備 betweenC
   * @returns {object}
   */
  getBetweenCentralityPre() {
    let betweenC;
    if (!this.betweenC) {
      betweenC = getBetweenCentrality(this.graph);
      this.betweenC = betweenC;
    } else {
      betweenC = this.betweenC;
    }
    return betweenC;
  }

  /**
   * 介數中心性(betweenness centrality)
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getBetweenCentrality() {
    const betweenC = this.getBetweenCentralityPre();
    return transformList(orderByValue(getIdValueList(betweenC)));
  }

  /**
   * 介數中心性(betweenness centrality)-標準化
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getBetweenCentralityStd() {
    const betweenC = this.getBetweenCentralityPre();
    // @ts-ignore
    const total = Object.values(betweenC).reduce(getTotalReduce, 0);
    const betweenStd = Object.entries(betweenC).reduce((acc, [key, val]) => {
      // @ts-ignore
      acc[key] = (val || 0) / total;
      return acc;
    }, {});
    return transformList(orderByValue(getIdValueList(betweenStd)));
  }

  /**
   * 接近中心性(closeness centrality)
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getCloseCentrality() {
    let closeC;
    if (!this.closeC) {
      closeC = getCloseCentrality(this.graph);
      this.closeC = closeC;
    } else {
      closeC = this.closeC;
    }
    return transformList(orderByValue(getIdValueList(closeC)));
    // return getIdValueList(closeC);
  }

  /**
   * 離中心性(eccentricity centrality)
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getEccentriCentrality() {
    let eccenC;
    if (!this.eccenC) {
      eccenC = getEccentricityCentrality(this.graph);
      this.eccenC = eccenC;
    } else {
      eccenC = this.eccenC;
    }
    return transformList(orderByValue(getIdValueList(eccenC)));
  }

  /**
   * 特徵向量中心性 (Eigenvector centrality)
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getEigenvectorCentrality() {
    let tmpEgvC;
    if (!this.egvC) {
      tmpEgvC = getEigenvectorCentrality(this.graph);
      this.egvC = tmpEgvC;
    } else {
      tmpEgvC = this.egvC;
    }
    return transformList(orderByValue(getIdValueList(tmpEgvC)));
  }

  /**
   * 取得 page rank
   * @returns {import('./snaAnalysis').ValueTransformList}
   */
  getPageRank() {
    let tmpPageRank;
    if (!this.pageRank) {
      tmpPageRank = getPageRank(this.graph);
      this.pageRank = tmpPageRank;
    } else {
      tmpPageRank = this.pageRank;
    }
    return transformList(orderByValue(getIdValueList(tmpPageRank)));
  }

  /**
   * 取得 gephi format
   * @returns {string|*}
   */
  getGexfFileContent() {
    if (this.graph) {
      return gexf.save(this.graph);
    }
    return "";
  }
}

export default GraphAnalysis;
